import React from 'react';
import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
} from "react-router-dom";
import { AuthProvider, FirebaseAppProvider, FirestoreProvider, StorageProvider } from 'reactfire';
import { firebaseConfig } from './utils/firebaseConfig';
import { PrivateRoute } from './components/PrivateRoute';
import { connectAuthEmulator, getAuth, indexedDBLocalPersistence, initializeAuth } from 'firebase/auth';
import config from './utils/config';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { ToastContainer } from 'react-toastify';

import './styles/App.scss';
import "bootstrap-icons/font/bootstrap-icons.css"
import 'react-toastify/dist/ReactToastify.css';
import { PipelinePage } from './pages/PipelinePage';
import { PATHS } from './utils/shared/constants';
import { PipelineEditPage } from './pages/PipelineEditPage';
import MapPage from './pages/MapPage';
import CardEditPage from './pages/CardEditPage';
import UserPreferencesPage from './pages/UserPreferencesPage';
import SearchPage from './pages/SearchPage';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import CustomersPage from './pages/CustomersPage';
import CustomerWritePage from './pages/CustomerWritePage';
import CustomerPage from './pages/CustomerPage';
import ArticlesPage from './pages/ArticlesPage';
import ArticleWritePage from './pages/ArticleWritePage';
import SuperAdminPage from './pages/SuperAdminPage';
import SuperAdminUserPage from './pages/SuperAdminUserPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import NotFoundPage from './pages/NotFoundPage';
import SharedDealPage from './pages/SharedDealPage';
import OffersEditorPage from './pages/OffersEditorPage';
import OfferPage from './pages/OfferPage';
import InvoiceEditorPage from './pages/InvoiceEditorPage';
import InvoicePage from './pages/InvoicePage';
import { initializeApp } from 'firebase/app';
import PublicNewDealPage from './pages/PublicNewDealPage';
import IntegrationsPage from './pages/IntegrationsPage';
import OrganizationSettingsPage from './pages/OrganizationSettingsPage';
import TimeSheetsPage from './pages/TimeSheetsPage';
import SupportPage from './pages/SupportPage';
import DealTimeLinePage from './pages/DealTimeLinePage';
import EquipmentPage from './pages/EquipmentPage';
import EquipmentWritePage from './pages/EquipmentWritePage';
import UpdateBanner from './components/UpdateBanner';

const router = createBrowserRouter([
  {
    path: "/",
    element: <PrivateRoute fullScreen><Outlet /></PrivateRoute>,
    children: [
      { index: true, element: <PipelinePage /> },
      { path: `${PATHS.DEALS}/:dealId`, element: <PipelinePage /> },
      { path: PATHS.MAP, element: <MapPage /> },
      { path: PATHS.PIPELINE_EDIT, element: <PipelineEditPage /> },
    ]
  },
  {
    path: "/",
    element: <PrivateRoute><Outlet /></PrivateRoute>,
    children: [
      { path: PATHS.CARD_EDIT, element: <CardEditPage /> },
      { path: PATHS.ORGANIZATIONSETTINGS, element: <OrganizationSettingsPage /> },
      { path: PATHS.USER_PREFERENCES, element: <UserPreferencesPage /> },
      { path: PATHS.CUSTOMERS, element: <CustomersPage /> },
      { path: `${PATHS.CUSTOMERS}/:customerId`, element: <CustomerPage /> },
      { path: `${PATHS.CUSTOMERS_EDIT}/:customerId?`, element: <CustomerWritePage /> },
      { path: PATHS.ARTICLES, element: <ArticlesPage /> },
      { path: `${PATHS.ARTICLES_EDIT}/:articleId?`, element: <ArticleWritePage /> },
      { path: PATHS.EQUIPMENT, element: <EquipmentPage /> },
      { path: `${PATHS.EQUIPMENT_EDIT}/:itemId?`, element: <EquipmentWritePage /> },
      { path: PATHS.TIMESHEETS, element: <TimeSheetsPage /> },
      { path: PATHS.SUPPORT, element: <SupportPage /> },
      { path: PATHS.SUPER_ADMIN, element: <SuperAdminPage /> },
      { path: `${PATHS.SUPER_ADMIN}/:uid`, element: <SuperAdminUserPage /> },
      { path: `${PATHS.DEALS}/:dealId${PATHS.OFFERS}`, element: <OffersEditorPage /> },
      { path: `${PATHS.DEALS}/:dealId${PATHS.INVOICES}/:invoiceId`, element: <InvoiceEditorPage /> },
      { path: PATHS.INTEGRATIONS, element: <IntegrationsPage /> },
      { path: `${PATHS.DEAL_TIMELINE}/:dealId`, element: <DealTimeLinePage /> }
    ]
  },
  {
    path: PATHS.SEARCH,
    element: <PrivateRoute noSearch><SearchPage /></PrivateRoute>,
  },
  {
    path: PATHS.INVITE,
    element: <PrivateRoute isInvite><InviteAcceptPage /></PrivateRoute>,
  },
  {
    path: `${PATHS.SHARED}/:tokenId`,
    element: <SharedDealPage />,
  },
  {
    path: `${PATHS.OFFERS}/:tokenId`,
    element: <OfferPage />,
  },
  {
    path: `${PATHS.INVOICES}/:tokenId`,
    element: <InvoicePage />,
  },
  {
    path: `${PATHS.NEW}/:orgId`,
    element: <PublicNewDealPage />
  },
  {
    path: "*",
    element: <NotFoundPage />
  }
]);


const firebaseApp = initializeApp(firebaseConfig);
// getAuth() can hang in a native WebView, so the native app initializes auth with explicit persistence
const auth = config.isNativeApp
  ? initializeAuth(firebaseApp, { persistence: indexedDBLocalPersistence })
  : getAuth(firebaseApp);
const firestore = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp)

if (config.useEmulators) {
  console.log('Connecting to emulators');
  connectFirestoreEmulator(firestore, 'localhost', 8080);
  connectAuthEmulator(auth, "http://localhost:9099")
  connectStorageEmulator(storage, 'localhost', 9199)
}

function App() {
  return (
    <FirebaseAppProvider firebaseConfig={firebaseConfig}>
      <AuthProvider sdk={auth}>
        <FirestoreProvider sdk={firestore}>
          <StorageProvider sdk={storage}>
            <RouterProvider router={router} />
            <ToastContainer position='bottom-center' stacked />
            <UpdateBanner />
          </StorageProvider>
        </FirestoreProvider>
      </AuthProvider>
    </FirebaseAppProvider>
  );
}

export default App;
